import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SubscriptionPlanService, SubscriptionPlanDTO } from '../../services/subscription-plan.service';
import { CondominiumService, CondominiumSubscriptionInfo } from '../../services/condominium.service';
import { GenericConfirmationDialogComponent } from '../delete-confirmation-dialog/generic-confirmation-dialog.component';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-subscription-plan-selector',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule
  ],
  templateUrl: './subscription-plan-selector.component.html',
  styleUrl: './subscription-plan-selector.component.sass'
})
export class SubscriptionPlanSelectorComponent implements OnInit, OnChanges {
  @Input() currentSubscriptionPlan?: CondominiumSubscriptionInfo;
  @Input() condominiumId?: number;
  @Output() planSelected = new EventEmitter<SubscriptionPlanDTO | null>();
  @Output() subscriptionCancelled = new EventEmitter<void>();

  allPlans: SubscriptionPlanDTO[] = [];
  plans: SubscriptionPlanDTO[] = [];
  selectedPlan: SubscriptionPlanDTO | null = null;
  isLoading = false;
  isCancellingSubscription = false;

  constructor(
    private subscriptionPlanService: SubscriptionPlanService,
    private condominiumService: CondominiumService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadPlans();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentSubscriptionPlan']) {
      if (
        this.selectedPlan != null &&
        this.currentSubscriptionPlan?.id === this.selectedPlan.id
      ) {
        this.selectedPlan = null;
        this.planSelected.emit(null);
      }
      if (this.allPlans.length > 0) {
        this.applyPlanFilter();
      }
    }
  }

  loadPlans(): void {
    this.isLoading = true;
    this.subscriptionPlanService.getSubscriptionPlans(true).subscribe({
      next: (plans) => {
        this.allPlans = plans.sort((a, b) => {
          if (a.maxUsers === null || a.maxUsers === undefined) return 1;
          if (b.maxUsers === null || b.maxUsers === undefined) return -1;
          return a.maxUsers - b.maxUsers;
        });
        this.applyPlanFilter();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private applyPlanFilter(): void {
    const currentId = this.currentSubscriptionPlan?.id;
    this.plans =
      currentId != null
        ? this.allPlans.filter((p) => p.id !== currentId)
        : [...this.allPlans];
  }

  openCancelConfirmation(): void {
    if (!this.condominiumId || this.currentSubscriptionPlan?.deactivationDate) {
      return;
    }
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: {
        message: 'Você está cancelando seu plano. Continuar?',
        confirmButtonText: 'Ok',
        cancelButtonText: 'Cancelar',
        cancelButtonStyle: 'raised',
        cancelButtonColor: 'primary',
        confirmButtonColor: 'warn'
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.cancelSubscription();
      }
    });
  }

  private cancelSubscription(): void {
    if (!this.condominiumId) {
      return;
    }
    this.isCancellingSubscription = true;
    this.condominiumService.cancelSubscription(this.condominiumId).subscribe({
      next: (res) => {
        this.isCancellingSubscription = false;
        const endDateFormatted = this.formatEndDate(res.endDate);
        this.dialog
          .open(AlertDialogComponent, {
            data: {
              message: `Plano cancelado. Você ainda pode usar o Green Village até ${endDateFormatted}.`,
              okText: 'Ok'
            }
          })
          .afterClosed()
          .subscribe(() => {
            this.subscriptionCancelled.emit();
          });
      },
      error: () => {
        this.isCancellingSubscription = false;
        this.snackBar.open('Não foi possível cancelar o plano.', 'Fechar', { duration: 5000 });
      }
    });
  }

  formattedDeactivationDate(): string {
    const iso = this.currentSubscriptionPlan?.deactivationDate;
    if (!iso) {
      return '';
    }
    return this.formatEndDate(iso);
  }

  private formatEndDate(iso: string): string {
    const part = iso.split('T')[0];
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  onPlanClick(plan: SubscriptionPlanDTO): void {
    if (this.selectedPlan?.id === plan.id) {
      this.selectedPlan = null;
      this.planSelected.emit(null);
    } else {
      this.selectedPlan = plan;
      this.planSelected.emit(plan);
    }
  }

  isPlanSelected(plan: SubscriptionPlanDTO): boolean {
    return this.selectedPlan?.id === plan.id;
  }
}
